'use client';

import { cn } from '@core/utils';
import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { debounce } from 'es-toolkit';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

type AutoplayOptions = {
  delay?: number;
  stopOnInteraction?: boolean;
};

interface VerticalCarouselProps {
  children: ReactNode;
  className?: string;
  slideClassName?: string;
  loop?: boolean;
  autoplay?: boolean | AutoplayOptions;
  align?: 'start' | 'center' | 'end';
  dragFree?: boolean;
  height?: number;
}

const VerticalCarouselViewportContext = React.createContext<HTMLElement | null>(
  null,
);

const useVerticalCarouselViewport = () =>
  React.useContext(VerticalCarouselViewportContext);

const VerticalCarouselBase = ({
  children,
  className,
  loop = false,
  slideClassName = 'mb-8',
  autoplay = false,
  align = 'start',
  dragFree = true,
}: VerticalCarouselProps) => {
  const options: EmblaOptionsType = {
    axis: 'y',
    loop,
    align,
    dragFree: !loop && dragFree,
    containScroll: loop ? false : 'trimSnaps',
    slidesToScroll: 1,
  };

  const plugins = autoplay
    ? [
        Autoplay(
          typeof autoplay === 'object'
            ? autoplay
            : { delay: 3000, stopOnInteraction: false },
        ),
      ]
    : [];

  const [emblaRef] = useEmblaCarousel(options, plugins);
  const [viewportNode, setViewportNode] = useState<HTMLElement | null>(null);

  const handleViewportNodeRef = useCallback((node: HTMLElement | null) => {
    setViewportNode(node);
  }, []);

  return (
    <VerticalCarouselViewportContext.Provider value={viewportNode}>
      <section
        className={cn('mx-auto w-full overflow-hidden', className)}
        ref={handleViewportNodeRef}
      >
        <div className="h-full overflow-hidden" ref={emblaRef}>
          <div
            className={cn(
              'flex h-full flex-col',
              '[touch-action:pan-y_pinch-zoom]',
            )}
          >
            {React.Children.map(children, (child) => (
              <div className={cn('mb-8', slideClassName)}>{child}</div>
            ))}
          </div>
        </div>
      </section>
    </VerticalCarouselViewportContext.Provider>
  );
};

interface VisibilityViewPortItemProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  hiddenClassName?: string;
  visibleClassName?: string;
  transitionClassName?: string;
  threshold?: number;
}

const VisibilityViewPortItem = ({
  children,
  className,
  contentClassName,
  hiddenClassName = 'opacity-0 pointer-events-none',
  visibleClassName = 'opacity-100',
  transitionClassName = 'transition-opacity duration-300 ease-out',
  threshold = 0.95,
}: VisibilityViewPortItemProps) => {
  const viewport = useVerticalCarouselViewport();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const visibilityDebounceRef = useRef(
    debounce((visible: boolean) => {
      setIsVisible(visible);
    }, 10),
  );

  useEffect(() => {
    const node = containerRef.current;

    if (!node) return;

    if (!viewport) {
      setIsVisible(true);

      return;
    }

    const visibilityDebounce = visibilityDebounceRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        visibilityDebounce(entry.isIntersecting);
      },
      {
        root: viewport,
        threshold,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();

      visibilityDebounce.cancel?.();
    };
  }, [threshold, viewport]);

  const visibilityClassName = cn(
    transitionClassName,
    contentClassName,
    isVisible ? visibleClassName : hiddenClassName,
  );

  return (
    <div ref={containerRef} className={className}>
      <div className={visibilityClassName}>{children}</div>
    </div>
  );
};

type VerticalCarouselCompoundComponent = typeof VerticalCarouselBase & {
  VisibilityViewPortItem: typeof VisibilityViewPortItem;
};

const VerticalCarousel =
  VerticalCarouselBase as VerticalCarouselCompoundComponent;
VerticalCarousel.VisibilityViewPortItem = VisibilityViewPortItem;


